export const ANY_OPTION = 'Any';

const TOP_OPTION_SUFFIX = '+';

function parseOptionValue(option) {
  return parseInt(option, 10);
}

// The top option ('5+' for Bedrooms/Bathrooms, '3+' for Garages/Stories) is the only one written
// with a trailing '+': it already means "that value or more", so it never becomes an exact value
// and is never stored as a range's upper bound.
function isTopOption(option) {
  return option.endsWith(TOP_OPTION_SUFFIX);
}

const ANY_STATE = { min: null, max: null };

// Open-ended state ({ min, max: null }): everything from `min` up to the top option is highlighted.
function getNextOpenEndedState(min, clickedValue, isTop) {
  if (clickedValue === min) {
    // The top option alone toggles straight back to Any; any other value narrows to exactly itself.
    return isTop ? ANY_STATE : { min, max: min };
  }

  if (clickedValue < min) {
    return { min: clickedValue, max: null };
  }

  // Above the start: the top option keeps only itself; anything between becomes the new end.
  return isTop ? { min: clickedValue, max: null } : { min, max: clickedValue };
}

// Exact value ({ min, max } with min === max): a second click on it resets, any other value
// extends the range toward it (up to the top option, which leaves the range open-ended).
function getNextExactState(value, clickedValue, isTop) {
  if (clickedValue === value) {
    return ANY_STATE;
  }

  if (clickedValue < value) {
    return { min: clickedValue, max: value };
  }

  return { min: value, max: isTop ? null : clickedValue };
}

// Bounded range ({ min, max } with min < max).
function getNextBoundedState({ min, max }, clickedValue, isTop) {
  if (clickedValue === min || clickedValue === max) {
    return { min: clickedValue, max: clickedValue };
  }

  if (clickedValue < min) {
    return { min: clickedValue, max };
  }

  if (clickedValue < max) {
    return { min, max: clickedValue };
  }

  return { min, max: isTop ? null : clickedValue };
}

/**
 * Pure state transition for the "Smart Range Selection" interaction shared by filters like
 * Bedrooms, Bathrooms, Garages and Stories/Floors: a row of Any/1/2/3/4/5+-style buttons whose
 * selection is a minimum ("N or more"), a range ("N through M") or an exact value.
 *
 * State is `{ min, max }`, both `number | null`: `{ null, null }` is Any (the default),
 * `{ N, null }` is "N or more", `{ N, M }` is "N through M" and `{ N, N }` is "exactly N". A click:
 * - on Any always resets to Any.
 * - from Any, selects that value and everything above it ("N or more").
 * - below the start of the selection moves the start down to it; the end stays.
 * - strictly inside the selection makes it the new end.
 * - above the end of a range or an exact value moves the end up to it; reaching the top option
 *   leaves the range open-ended ("2–4" → "5+" gives "2 or more").
 * - on either end of a range, or on the start of an open-ended selection, selects exactly that value.
 * - on the exact value again resets to Any, so the same value clicked three times goes
 *   "N or more" → "exactly N" → Any.
 * - on the top option from an open-ended selection keeps only the top option; clicking it again
 *   resets to Any — there is no "exactly 5".
 *
 * @param {{ min: number|null, max: number|null }} state
 * @param {string} clickedOption - The option's own text, e.g. '3', '5+' or 'Any'.
 * @returns {{ min: number|null, max: number|null }}
 */
export function getNextRangeState(state, clickedOption) {
  if (clickedOption === ANY_OPTION) {
    return ANY_STATE;
  }

  const clickedValue = parseOptionValue(clickedOption);
  const isTop = isTopOption(clickedOption);

  if (state.min === null) {
    return { min: clickedValue, max: null };
  }

  if (state.max === null) {
    return getNextOpenEndedState(state.min, clickedValue, isTop);
  }

  if (state.min === state.max) {
    return getNextExactState(state.min, clickedValue, isTop);
  }

  return getNextBoundedState(state, clickedValue, isTop);
}

/**
 * Whether `option` should be highlighted for the given range state.
 *
 * @param {string} option
 * @param {{ min: number|null, max: number|null }} state
 * @returns {boolean}
 */
export function isOptionInRange(option, { min, max }) {
  if (option === ANY_OPTION) {
    return min === null;
  }

  if (min === null) {
    return false;
  }

  const value = parseOptionValue(option);
  return max === null ? value >= min : value >= min && value <= max;
}
